import { LastInteractionTagFilterConfig } from '@/api/generated/model';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { filterToOperator, FilterOperator, getOperatorLabel } from '@/comps/FilterPicker/filter-operators';
import { FILTERS } from '@/models/common/filters';
import { humanizeString } from '@/utils/string.utils';

export type LastInteractionTagFilterState = {
  tag: string | null;
  operator: FilterOperator;
  values: string[];
  select_all?: boolean;
  search?: string;
};

export function isLastInteractionTagFilter(column: string | null | undefined): boolean {
  return column === FILTERS.lastInteractionTag;
}

export function createDefaultLastInteractionTagState(): LastInteractionTagFilterState {
  return {
    tag: null,
    operator: 'equals',
    values: [],
  };
}

export function parseLastInteractionTagFilter(filter: GenericFilter | undefined): LastInteractionTagFilterState {
  if (!filter || !isLastInteractionTagFilter(filter.column)) {
    return createDefaultLastInteractionTagState();
  }

  const tagFilter = filter as GenericFilter & { tag?: string };
  return {
    tag: tagFilter.tag ?? null,
    operator: filterToOperator(filter),
    values: Array.isArray(filter.values) ? filter.values : [],
    select_all: filter.select_all,
    search: filter.search ?? undefined,
  };
}

export function serializeLastInteractionTagFilter(
  state: LastInteractionTagFilterState,
): LastInteractionTagFilterConfig | null {
  if (!state.tag) return null;

  if (state.operator === 'contains') {
    const search = state.search?.trim();
    if (!search) return null;

    return {
      column: FILTERS.lastInteractionTag,
      tag: state.tag,
      values: [],
      is_exclude: false,
      search,
    };
  }

  if (!state.select_all && state.values.length === 0) return null;

  return {
    column: FILTERS.lastInteractionTag,
    tag: state.tag,
    values: state.values,
    is_exclude: state.operator === 'is_not',
    select_all: state.select_all,
  };
}

export function formatLastInteractionTagPillSummary(filter: GenericFilter | undefined): string | undefined {
  const state = parseLastInteractionTagFilter(filter);
  if (!state.tag) return undefined;

  const tagName = humanizeString(state.tag);
  const valuesLabel = state.select_all
    ? 'All'
    : state.search?.trim()
      ? state.search.trim()
      : state.values.map((value) => humanizeString(value)).join(', ');

  if (!valuesLabel && state.operator !== 'contains') return undefined;

  return `${tagName} ${getOperatorLabel(state.operator).toLowerCase()}: ${valuesLabel}`;
}
